import React, { useState, useRef, Component } from 'react';
import { PencilIcon, TrashIcon, PlusIcon, SearchIcon, CalendarIcon, ClockIcon, TagIcon, FileTextIcon, SaveIcon, ChevronLeftIcon, ImageIcon, LockIcon, UnlockIcon, UserIcon, BellIcon, XIcon, UploadIcon, MapPinIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import Button from './common/Button';
// Function to generate Google Street View image URL
const getStreetViewImageUrl = address => {
  // In a real app, you would use your actual API key
  // For demo purposes, we're using a placeholder URL
  const encodedAddress = encodeURIComponent(address || '123 Main St, Anytown, USA');
  return `https://maps.googleapis.com/maps/api/streetview?size=600x400&location=${encodedAddress}&key=YOUR_API_KEY`;
};
// Mock data for notes and logs
const mockNotes = [{
  id: 'note1',
  title: 'Initial Property Assessment',
  content: 'Conducted initial walkthrough of the property. The foundation appears solid, but there are significant issues with the roof and electrical systems that will need to be addressed. The kitchen and bathrooms are outdated but functional.',
  date: '2023-05-15',
  time: '10:30 AM',
  tags: ['Assessment', 'Renovation'],
  type: 'note',
  user: {
    id: 'user1',
    name: 'John Smith',
    avatar: 'https://randomuser.me/api/portraits/men/1.jpg'
  },
  isPrivate: false,
  notify: {
    internal: false,
    subcontractors: false,
    vendors: false,
    clients: false
  },
  photos: [],
  useStreetView: true,
  address: '123 Main St, Anytown, USA',
  lastUpdated: '2023-05-15 10:30 AM'
}, {
  id: 'log1',
  title: 'Contractor Meeting',
  content: 'Met with John from ABC Contractors to discuss renovation timeline and budget. He suggested starting with the roof repairs before moving to interior work. Estimated timeline: 3-4 months for complete renovation.',
  date: '2023-05-20',
  time: '2:00 PM',
  tags: ['Contractor', 'Meeting'],
  type: 'log',
  user: {
    id: 'user2',
    name: 'Sarah Johnson',
    avatar: 'https://randomuser.me/api/portraits/women/2.jpg'
  },
  isPrivate: false,
  notify: {
    internal: true,
    subcontractors: true,
    vendors: false,
    clients: false
  },
  photos: [{
    id: 'photo1',
    url: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?ixlib=rb-1.2.1&auto=format&fit=crop&w=1489&q=80',
    caption: 'Site conditions before work'
  }],
  useStreetView: false,
  address: '456 Oak Ave, Anytown, USA',
  lastUpdated: '2023-05-20 2:00 PM'
}, {
  id: 'note2',
  title: 'Budget Considerations',
  content: 'After reviewing contractor bids, we need to allocate an additional $15,000 for unexpected electrical work. This will impact our overall ROI but is necessary for safety and code compliance.',
  date: '2023-05-25',
  time: '4:15 PM',
  tags: ['Budget', 'Planning'],
  type: 'note',
  user: {
    id: 'user1',
    name: 'John Smith',
    avatar: 'https://randomuser.me/api/portraits/men/1.jpg'
  },
  isPrivate: true,
  notify: {
    internal: true,
    subcontractors: false,
    vendors: false,
    clients: false
  },
  photos: [],
  useStreetView: true,
  address: '789 Pine Rd, Anytown, USA',
  lastUpdated: '2023-05-25 4:15 PM'
}, {
  id: 'log2',
  title: 'Permit Application Submitted',
  content: 'Submitted all required permit applications to the city. Expecting a 2-3 week turnaround time for approval. Paid $2,500 in permit fees.',
  date: '2023-06-01',
  time: '11:45 AM',
  tags: ['Permits', 'Administrative'],
  type: 'log',
  user: {
    id: 'user3',
    name: 'Michael Brown',
    avatar: 'https://randomuser.me/api/portraits/men/3.jpg'
  },
  isPrivate: false,
  notify: {
    internal: true,
    subcontractors: false,
    vendors: false,
    clients: true
  },
  photos: [],
  useStreetView: true,
  address: '101 Maple Dr, Anytown, USA',
  lastUpdated: '2023-06-01 11:45 AM'
}, {
  id: 'log3',
  title: 'Demolition Started',
  content: "Demolition crew began work today. Removed all kitchen cabinets and appliances. Discovered some water damage behind the refrigerator that wasn't previously noted. Will need to assess impact on budget.",
  date: '2023-06-15',
  time: '9:00 AM',
  tags: ['Construction', 'Demolition'],
  type: 'log',
  user: {
    id: 'user2',
    name: 'Sarah Johnson',
    avatar: 'https://randomuser.me/api/portraits/women/2.jpg'
  },
  isPrivate: false,
  notify: {
    internal: true,
    subcontractors: true,
    vendors: false,
    clients: false
  },
  photos: [{
    id: 'photo2',
    url: 'https://images.unsplash.com/photo-1544984243-ec57ea16fe25?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    caption: 'Kitchen demolition in progress'
  }, {
    id: 'photo3',
    url: 'https://images.unsplash.com/photo-1600607686527-6fb886090705?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    caption: 'Water damage discovered behind refrigerator'
  }],
  useStreetView: false,
  address: '202 Elm St, Anytown, USA',
  lastUpdated: '2023-06-15 9:00 AM'
}];
// Current user mock
const currentUser = {
  id: 'user1',
  name: 'John Smith',
  avatar: 'https://randomuser.me/api/portraits/men/1.jpg'
};
const Notes = () => {
  const [notes, setNotes] = useState(mockNotes);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedNote, setSelectedNote] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [filterType, setFilterType] = useState('all'); // 'all', 'note', or 'log'
  // New note template
  const newNoteTemplate = {
    id: `note${Date.now()}`,
    title: 'New Note',
    content: '',
    date: new Date().toISOString().split('T')[0],
    time: new Date().toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    }),
    tags: [],
    type: 'note',
    user: currentUser,
    isPrivate: false,
    notify: {
      internal: false,
      subcontractors: false,
      vendors: false,
      clients: false
    },
    photos: [],
    useStreetView: true,
    address: '',
    lastUpdated: new Date().toLocaleString()
  };
  // Filter notes based on search term and type
  const filteredNotes = notes.filter(note => (filterType === 'all' || note.type === filterType) && (note.title.toLowerCase().includes(searchTerm.toLowerCase()) || note.content.toLowerCase().includes(searchTerm.toLowerCase()) || note.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))));
  const handleCreateNote = () => {
    const newNote = {
      ...newNoteTemplate
    };
    setNotes([newNote, ...notes]);
    setSelectedNote(newNote);
    setIsEditing(true);
  };
  const handleSaveNote = updatedNote => {
    // Update the lastUpdated field
    updatedNote.lastUpdated = new Date().toLocaleString();
    updatedNote.user = currentUser;
    setNotes(notes.map(note => note.id === updatedNote.id ? updatedNote : note));
    setSelectedNote(updatedNote);
    setIsEditing(false);
  };
  const handleDeleteNote = noteId => {
    setNotes(notes.filter(note => note.id !== noteId));
    setSelectedNote(null);
  };
  const handleBackToList = () => {
    setSelectedNote(null);
    setIsEditing(false);
  };
  return <div className="max-w-7xl mx-auto" data-id="element-872">
      <h1 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-873">
        Notes & Daily Logs
      </h1>
      <div className="flex flex-col lg:flex-row gap-6" data-id="element-874">
        {/* Sidebar */}
        <div className="w-full lg:w-64 flex-shrink-0" data-id="element-875">
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4" data-id="element-876">
            <div className="flex justify-between items-center mb-4" data-id="element-877">
              <h2 className="text-lg font-medium text-gray-900" data-id="element-878">Notes</h2>
              <Button size="sm" onClick={handleCreateNote} data-id="element-879">
                <PlusIcon className="w-4 h-4 mr-1" data-id="element-880" />
                New
              </Button>
            </div>
            <div className="mb-4" data-id="element-881">
              <div className="relative" data-id="element-882">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-883">
                  <SearchIcon className="h-4 w-4 text-gray-400" data-id="element-884" />
                </div>
                <input type="text" className="block w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded-md text-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Search notes..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-885" />
              </div>
            </div>
            <div className="mb-4" data-id="element-886">
              <div className="flex border border-gray-200 rounded-md overflow-hidden" data-id="element-887">
                <button className={`flex-1 py-1.5 text-sm font-medium ${filterType === 'all' ? 'bg-blue-50 text-blue-600' : 'bg-white text-gray-500 hover:bg-gray-50'}`} onClick={() => setFilterType('all')} data-id="element-888">
                  All
                </button>
                <button className={`flex-1 py-1.5 text-sm font-medium ${filterType === 'note' ? 'bg-blue-50 text-blue-600' : 'bg-white text-gray-500 hover:bg-gray-50'}`} onClick={() => setFilterType('note')} data-id="element-889">
                  Notes
                </button>
                <button className={`flex-1 py-1.5 text-sm font-medium ${filterType === 'log' ? 'bg-blue-50 text-blue-600' : 'bg-white text-gray-500 hover:bg-gray-50'}`} onClick={() => setFilterType('log')} data-id="element-890">
                  Logs
                </button>
              </div>
            </div>
            <div className="space-y-1 mt-2" data-id="element-891">
              {filteredNotes.map(note => <button key={note.id} className={`w-full flex items-start px-3 py-2 rounded-md text-left ${selectedNote && selectedNote.id === note.id ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100'}`} onClick={() => {
              setSelectedNote(note);
              setIsEditing(false);
            }} data-id="element-892">
                  <div className={`mt-0.5 p-1 rounded-full flex-shrink-0 ${note.type === 'log' ? 'bg-purple-100' : 'bg-blue-100'}`} data-id="element-893">
                    {note.type === 'log' ? <ClockIcon className="w-3 h-3 text-purple-600" data-id="element-894" /> : <PencilIcon className="w-3 h-3 text-blue-600" data-id="element-895" />}
                  </div>
                  <div className="ml-2 overflow-hidden" data-id="element-896">
                    <div className="flex items-center" data-id="element-897">
                      <p className="truncate font-medium text-sm" data-id="element-898">
                        {note.title}
                      </p>
                      {note.isPrivate && <LockIcon className="w-3 h-3 ml-1 text-gray-500" data-id="element-899" />}
                    </div>
                    <p className="text-xs text-gray-500 truncate" data-id="element-900">
                      {note.date} • {note.user.name}
                    </p>
                  </div>
                </button>)}
              {filteredNotes.length === 0 && <div className="text-center py-4 text-gray-500 text-sm" data-id="element-901">
                  No notes found
                </div>}
            </div>
            <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-902">
              <Link to="/file-cabinet" className="w-full flex items-center px-3 py-2 rounded-md text-left text-gray-700 hover:bg-gray-100" data-id="element-903">
                <FileTextIcon className="w-5 h-5 mr-2 text-gray-400" data-id="element-904" />
                <span data-id="element-905">File Cabinet</span>
              </Link>
              <Link to="/todos" className="w-full flex items-center px-3 py-2 rounded-md text-left text-gray-700 hover:bg-gray-100" data-id="element-906">
                <TagIcon className="w-5 h-5 mr-2 text-gray-400" data-id="element-907" />
                <span data-id="element-908">To-Do Items</span>
              </Link>
            </div>
          </div>
        </div>
        {/* Main content area */}
        <div className="flex-1" data-id="element-909">
          {selectedNote ? isEditing ? <NoteEditor note={selectedNote} onSave={handleSaveNote} onCancel={handleBackToList} data-id="element-910" /> : <NoteViewer note={selectedNote} onEdit={() => setIsEditing(true)} onDelete={() => handleDeleteNote(selectedNote.id)} onBack={handleBackToList} data-id="element-911" /> : <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 text-center" data-id="element-912">
              <PencilIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" data-id="element-913" />
              <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-914">
                Select a note or create a new one
              </h3>
              <p className="text-gray-500 mb-4" data-id="element-915">
                Keep track of important information and daily activities
              </p>
              <Button onClick={handleCreateNote} data-id="element-916">
                <PlusIcon className="w-4 h-4 mr-2" data-id="element-917" />
                Create New Note
              </Button>
            </div>}
        </div>
      </div>
    </div>;
};
// Note Viewer Component
const NoteViewer = ({
  note,
  onEdit,
  onDelete,
  onBack
}) => {
  return <div className="bg-white border border-gray-200 rounded-lg shadow-sm" data-id="element-918">
      <div className="p-4 border-b border-gray-200" data-id="element-919">
        <div className="flex justify-between items-center" data-id="element-920">
          <div className="flex items-center" data-id="element-921">
            <button className="mr-3 text-gray-500 hover:text-gray-700" onClick={onBack} data-id="element-922">
              <ChevronLeftIcon className="w-5 h-5" data-id="element-923" />
            </button>
            <div className={`p-1 rounded-full mr-2 ${note.type === 'log' ? 'bg-purple-100' : 'bg-blue-100'}`} data-id="element-924">
              {note.type === 'log' ? <ClockIcon className="w-4 h-4 text-purple-600" data-id="element-925" /> : <PencilIcon className="w-4 h-4 text-blue-600" data-id="element-926" />}
            </div>
            <div data-id="element-927">
              <div className="flex items-center" data-id="element-928">
                <h2 className="text-lg font-medium text-gray-900" data-id="element-929">
                  {note.title}
                </h2>
                {note.isPrivate && <LockIcon className="w-4 h-4 ml-2 text-gray-500" title="Private" data-id="element-930" />}
              </div>
              <div className="flex items-center text-sm text-gray-500" data-id="element-931">
                <CalendarIcon className="w-3.5 h-3.5 mr-1" data-id="element-932" />
                <span data-id="element-933">
                  {note.date} at {note.time}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2" data-id="element-934">
            <Button size="sm" variant="outline" onClick={onEdit} data-id="element-935">
              <PencilIcon className="w-4 h-4 mr-1" data-id="element-936" />
              Edit
            </Button>
            <Button size="sm" variant="danger" onClick={onDelete} data-id="element-937">
              <TrashIcon className="w-4 h-4 mr-1" data-id="element-938" />
              Delete
            </Button>
          </div>
        </div>
      </div>
      <div className="p-6" data-id="element-939">
        <div className="mb-6" data-id="element-940">
          {note.tags.length > 0 && <div className="flex flex-wrap gap-1 mb-4" data-id="element-941">
              {note.tags.map((tag, index) => <span key={index} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs" data-id="element-942">
                  {tag}
                </span>)}
            </div>}
          <div className="prose max-w-none" data-id="element-943">
            <p className="whitespace-pre-line" data-id="element-944">{note.content}</p>
          </div>
        </div>
        {/* Photos section */}
        <div className="mt-6 border-t border-gray-200 pt-6" data-id="element-945">
          {/* Show user uploaded photos if available */}
          {note.photos && note.photos.length > 0 ? <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-946">
              {note.photos.map(photo => <div key={photo.id} className="relative group" data-id="element-947">
                  <img src={photo.url} alt={photo.caption || 'Note photo'} className="w-full h-40 object-cover rounded-md border border-gray-200" data-id="element-948" />
                  {photo.caption && <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-2 text-sm rounded-b-md" data-id="element-949">
                      {photo.caption}
                    </div>}
                </div>)}
            </div> : note.useStreetView && note.address /* Show Google Street View image if enabled and no user photos */ ? <div className="relative" data-id="element-950">
              <img src={getStreetViewImageUrl(note.address)} alt={`Street view of ${note.address}`} className="w-full h-64 object-cover rounded-md border border-gray-200" data-id="element-951" />
              <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-2 text-sm rounded-b-md flex items-center" data-id="element-952">
                <MapPinIcon className="w-4 h-4 mr-1" data-id="element-953" />
                <span data-id="element-954">{note.address}</span>
              </div>
              <div className="absolute top-2 right-2 bg-white px-2 py-1 rounded text-xs text-gray-500" data-id="element-955">
                Google Street View
              </div>
            </div> /* No photos available message */ : <div className="text-center py-8 border border-dashed border-gray-300 rounded-md bg-gray-50" data-id="element-956">
              <ImageIcon className="w-10 h-10 text-gray-300 mx-auto mb-2" data-id="element-957" />
              <p className="text-sm text-gray-500" data-id="element-958">No photos available</p>
            </div>}
        </div>
        {/* Notification settings */}
        {(note.notify.internal || note.notify.subcontractors || note.notify.vendors || note.notify.clients) && <div className="mt-6 border-t border-gray-200 pt-6" data-id="element-959">
            <h3 className="text-md font-medium text-gray-900 mb-2" data-id="element-960">
              Notifications
            </h3>
            <div className="flex flex-wrap gap-2" data-id="element-961">
              {note.notify.internal && <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs flex items-center" data-id="element-962">
                  <BellIcon className="w-3 h-3 mr-1" data-id="element-963" />
                  Internal Team
                </span>}
              {note.notify.subcontractors && <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs flex items-center" data-id="element-964">
                  <BellIcon className="w-3 h-3 mr-1" data-id="element-965" />
                  Subcontractors
                </span>}
              {note.notify.vendors && <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs flex items-center" data-id="element-966">
                  <BellIcon className="w-3 h-3 mr-1" data-id="element-967" />
                  Vendors
                </span>}
              {note.notify.clients && <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs flex items-center" data-id="element-968">
                  <BellIcon className="w-3 h-3 mr-1" data-id="element-969" />
                  Clients
                </span>}
            </div>
          </div>}
        {/* User and last updated info */}
        <div className="mt-6 border-t border-gray-200 pt-4 flex justify-between items-center text-sm text-gray-500" data-id="element-970">
          <div className="flex items-center" data-id="element-971">
            <img src={note.user.avatar} alt={note.user.name} className="w-6 h-6 rounded-full mr-2" data-id="element-972" />
            <span data-id="element-973">Updated by {note.user.name}</span>
          </div>
          <div data-id="element-974">Last updated: {note.lastUpdated}</div>
        </div>
      </div>
    </div>;
};
// Note Editor Component
const NoteEditor = ({
  note,
  onSave,
  onCancel
}) => {
  const [editedNote, setEditedNote] = useState({
    ...note
  });
  const [tagInput, setTagInput] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [photoCaption, setPhotoCaption] = useState('');
  const [editingPhotoId, setEditingPhotoId] = useState(null);
  const fileInputRef = useRef(null);
  const handleInputChange = e => {
    const {
      name,
      value
    } = e.target;
    setEditedNote({
      ...editedNote,
      [name]: value
    });
  };
  const handleTypeChange = type => {
    setEditedNote({
      ...editedNote,
      type
    });
  };
  const handlePrivacyChange = () => {
    setEditedNote({
      ...editedNote,
      isPrivate: !editedNote.isPrivate
    });
  };
  const handleStreetViewChange = () => {
    setEditedNote({
      ...editedNote,
      useStreetView: !editedNote.useStreetView
    });
  };
  const handleNotifyChange = type => {
    setEditedNote({
      ...editedNote,
      notify: {
        ...editedNote.notify,
        [type]: !editedNote.notify[type]
      }
    });
  };
  const handleAddTag = e => {
    e.preventDefault();
    if (tagInput.trim() && !editedNote.tags.includes(tagInput.trim())) {
      setEditedNote({
        ...editedNote,
        tags: [...editedNote.tags, tagInput.trim()]
      });
      setTagInput('');
    }
  };
  const handleRemoveTag = tagToRemove => {
    setEditedNote({
      ...editedNote,
      tags: editedNote.tags.filter(tag => tag !== tagToRemove)
    });
  };
  // Handle drag events
  const handleDrag = e => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };
  // Handle drop event
  const handleDrop = e => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };
  // Handle file input change
  const handleFileChange = e => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };
  // Process files
  const handleFiles = files => {
    // In a real app, we would upload these files to a server
    // For this demo, we'll create URLs from the files
    const newPhotos = Array.from(files).map(file => ({
      id: `photo-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      url: URL.createObjectURL(file),
      caption: '',
      name: file.name
    }));
    setEditedNote({
      ...editedNote,
      photos: [...editedNote.photos, ...newPhotos]
    });
  };
  // Handle photo caption
  const handleCaptionChange = (photoId, caption) => {
    setEditedNote({
      ...editedNote,
      photos: editedNote.photos.map(photo => photo.id === photoId ? {
        ...photo,
        caption
      } : photo)
    });
  };
  // Remove photo
  const handleRemovePhoto = photoId => {
    setEditedNote({
      ...editedNote,
      photos: editedNote.photos.filter(photo => photo.id !== photoId)
    });
  };
  // Preview of the street view image
  const streetViewPreview = editedNote.useStreetView && editedNote.address ? <div className="mt-4 relative" data-id="element-975">
        <img src={getStreetViewImageUrl(editedNote.address)} alt={`Street view of ${editedNote.address}`} className="w-full h-48 object-cover rounded-md border border-gray-200" data-id="element-976" />
        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-2 text-sm rounded-b-md flex items-center" data-id="element-977">
          <MapPinIcon className="w-4 h-4 mr-1" data-id="element-978" />
          <span data-id="element-979">{editedNote.address}</span>
        </div>
        <div className="absolute top-2 right-2 bg-white px-2 py-1 rounded text-xs text-gray-500" data-id="element-980">
          Google Street View
        </div>
      </div> : null;
  return <div className="bg-white border border-gray-200 rounded-lg shadow-sm" data-id="element-981">
      <div className="p-4 border-b border-gray-200" data-id="element-982">
        <div className="flex justify-between items-center" data-id="element-983">
          <div className="flex items-center" data-id="element-984">
            <button className="mr-3 text-gray-500 hover:text-gray-700" onClick={onCancel} data-id="element-985">
              <ChevronLeftIcon className="w-5 h-5" data-id="element-986" />
            </button>
            <h2 className="text-lg font-medium text-gray-900" data-id="element-987">
              {editedNote.id.includes('new') ? 'New Note' : 'Edit Note'}
            </h2>
          </div>
          <div className="flex items-center gap-2" data-id="element-988">
            <Button size="sm" variant="outline" onClick={onCancel} data-id="element-989">
              Cancel
            </Button>
            <Button size="sm" onClick={() => onSave(editedNote)} data-id="element-990">
              <SaveIcon className="w-4 h-4 mr-1" data-id="element-991" />
              Save
            </Button>
          </div>
        </div>
      </div>
      <div className="p-6" data-id="element-992">
        <div className="space-y-4" data-id="element-993">
          <div data-id="element-994">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-995">
              Title
            </label>
            <input type="text" name="title" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={editedNote.title} onChange={handleInputChange} data-id="element-996" />
          </div>
          <div className="flex items-center space-x-4" data-id="element-997">
            <div className="flex-1" data-id="element-998">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-999">
                Type
              </label>
              <div className="flex border border-gray-300 rounded-md overflow-hidden" data-id="element-1000">
                <button type="button" className={`flex-1 py-2 text-sm font-medium ${editedNote.type === 'note' ? 'bg-blue-50 text-blue-600' : 'bg-white text-gray-500 hover:bg-gray-50'}`} onClick={() => handleTypeChange('note')} data-id="element-1001">
                  Note
                </button>
                <button type="button" className={`flex-1 py-2 text-sm font-medium ${editedNote.type === 'log' ? 'bg-blue-50 text-blue-600' : 'bg-white text-gray-500 hover:bg-gray-50'}`} onClick={() => handleTypeChange('log')} data-id="element-1002">
                  Daily Log
                </button>
              </div>
            </div>
            <div data-id="element-1003">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1004">
                Privacy
              </label>
              <button type="button" onClick={handlePrivacyChange} className={`flex items-center justify-center w-full px-3 py-2 border ${editedNote.isPrivate ? 'bg-gray-100 border-gray-300 text-gray-700' : 'bg-white border-gray-300 text-gray-500'} rounded-md`} data-id="element-1005">
                {editedNote.isPrivate ? <>
                    <LockIcon className="w-4 h-4 mr-1" data-id="element-1006" />
                    Private
                  </> : <>
                    <UnlockIcon className="w-4 h-4 mr-1" data-id="element-1007" />
                    Public
                  </>}
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4" data-id="element-1008">
            <div data-id="element-1009">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1010">
                Date
              </label>
              <input type="date" name="date" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={editedNote.date} onChange={handleInputChange} data-id="element-1011" />
            </div>
            <div data-id="element-1012">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1013">
                Time
              </label>
              <input type="time" name="time" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={editedNote.time} onChange={handleInputChange} data-id="element-1014" />
            </div>
          </div>
          <div data-id="element-1015">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1016">
              Property Address
            </label>
            <input type="text" name="address" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={editedNote.address} onChange={handleInputChange} placeholder="Enter property address" data-id="element-1017" />
            <div className="mt-2" data-id="element-1018">
              <label className="inline-flex items-center" data-id="element-1019">
                <input type="checkbox" className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50" checked={editedNote.useStreetView} onChange={handleStreetViewChange} data-id="element-1020" />
                <span className="ml-2 text-sm text-gray-700" data-id="element-1021">
                  Use Google Street View image when no photos are uploaded
                </span>
              </label>
            </div>
            {/* Street View Preview */}
            {streetViewPreview}
          </div>
          <div data-id="element-1022">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1023">
              Tags
            </label>
            <div className="flex flex-wrap gap-2 mb-2" data-id="element-1024">
              {editedNote.tags.map((tag, index) => <div key={index} className="flex items-center bg-gray-100 text-gray-700 rounded-full px-3 py-1 text-sm" data-id="element-1025">
                  <span data-id="element-1026">{tag}</span>
                  <button type="button" className="ml-1 text-gray-500 hover:text-gray-700" onClick={() => handleRemoveTag(tag)} data-id="element-1027">
                    <XIcon className="w-3 h-3" data-id="element-1028" />
                  </button>
                </div>)}
            </div>
            <form onSubmit={handleAddTag} className="flex" data-id="element-1029">
              <input type="text" className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Add a tag..." value={tagInput} onChange={e => setTagInput(e.target.value)} data-id="element-1030" />
              <button type="submit" className="px-3 py-2 bg-gray-100 text-gray-700 border border-gray-300 border-l-0 rounded-r-md hover:bg-gray-200" data-id="element-1031">
                Add
              </button>
            </form>
          </div>
          <div data-id="element-1032">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1033">
              Content
            </label>
            <textarea name="content" rows={10} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={editedNote.content} onChange={handleInputChange} placeholder="Enter your note content here..." data-id="element-1034"></textarea>
          </div>
          {/* Photo Upload Section */}
          <div data-id="element-1035">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1036">
              Photos
            </label>
            <div className={`border-2 border-dashed rounded-md p-6 text-center ${dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`} onDragEnter={handleDrag} onDragOver={handleDrag} onDragLeave={handleDrag} onDrop={handleDrop} data-id="element-1037">
              <input ref={fileInputRef} type="file" multiple accept="image/*" onChange={handleFileChange} className="hidden" data-id="element-1038" />
              <div className="space-y-2" data-id="element-1039">
                <div className="mx-auto flex justify-center" data-id="element-1040">
                  <ImageIcon className="w-10 h-10 text-gray-400" data-id="element-1041" />
                </div>
                <div className="text-sm text-gray-600" data-id="element-1042">
                  <p data-id="element-1043">Drag and drop your photos here, or</p>
                  <button type="button" onClick={() => fileInputRef.current.click()} className="text-blue-600 hover:text-blue-700 font-medium" data-id="element-1044">
                    browse for files
                  </button>
                </div>
                <p className="text-xs text-gray-500" data-id="element-1045">
                  JPG, PNG or GIF files up to 10MB
                </p>
              </div>
            </div>
            {/* Photo Preview */}
            {editedNote.photos.length > 0 && <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-1046">
                {editedNote.photos.map(photo => <div key={photo.id} className="relative group" data-id="element-1047">
                    <img src={photo.url} alt={photo.caption || photo.name || 'Photo preview'} className="w-full h-40 object-cover rounded-md border border-gray-200" data-id="element-1048" />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all rounded-md flex items-center justify-center" data-id="element-1049">
                      <div className="opacity-0 group-hover:opacity-100 flex gap-2" data-id="element-1050">
                        <button type="button" onClick={() => setEditingPhotoId(photo.id)} className="p-1 bg-white rounded-full text-gray-700 hover:text-blue-600" data-id="element-1051">
                          <PencilIcon className="w-4 h-4" data-id="element-1052" />
                        </button>
                        <button type="button" onClick={() => handleRemovePhoto(photo.id)} className="p-1 bg-white rounded-full text-gray-700 hover:text-red-600" data-id="element-1053">
                          <TrashIcon className="w-4 h-4" data-id="element-1054" />
                        </button>
                      </div>
                    </div>
                    {photo.caption && <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-2 text-sm rounded-b-md" data-id="element-1055">
                        {photo.caption}
                      </div>}
                    {/* Caption Editor Modal */}
                    {editingPhotoId === photo.id && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-1056">
                        <div className="bg-white rounded-lg p-6 max-w-md w-full" data-id="element-1057">
                          <h3 className="text-lg font-medium mb-4" data-id="element-1058">
                            Edit Photo Caption
                          </h3>
                          <input type="text" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 mb-4" placeholder="Add a caption..." value={photo.caption || ''} onChange={e => handleCaptionChange(photo.id, e.target.value)} data-id="element-1059" />
                          <div className="flex justify-end gap-2" data-id="element-1060">
                            <Button size="sm" variant="outline" onClick={() => setEditingPhotoId(null)} data-id="element-1061">
                              Cancel
                            </Button>
                            <Button size="sm" onClick={() => setEditingPhotoId(null)} data-id="element-1062">
                              Save Caption
                            </Button>
                          </div>
                        </div>
                      </div>}
                  </div>)}
              </div>}
          </div>
          {/* Notification Options */}
          <div data-id="element-1063">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1064">
              Notify
            </label>
            <div className="flex flex-wrap gap-3" data-id="element-1065">
              <label className="inline-flex items-center" data-id="element-1066">
                <input type="checkbox" className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50" checked={editedNote.notify.internal} onChange={() => handleNotifyChange('internal')} data-id="element-1067" />
                <span className="ml-2 text-sm text-gray-700" data-id="element-1068">
                  Internal Team
                </span>
              </label>
              <label className="inline-flex items-center" data-id="element-1069">
                <input type="checkbox" className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50" checked={editedNote.notify.subcontractors} onChange={() => handleNotifyChange('subcontractors')} data-id="element-1070" />
                <span className="ml-2 text-sm text-gray-700" data-id="element-1071">
                  Subcontractors
                </span>
              </label>
              <label className="inline-flex items-center" data-id="element-1072">
                <input type="checkbox" className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50" checked={editedNote.notify.vendors} onChange={() => handleNotifyChange('vendors')} data-id="element-1073" />
                <span className="ml-2 text-sm text-gray-700" data-id="element-1074">Vendors</span>
              </label>
              <label className="inline-flex items-center" data-id="element-1075">
                <input type="checkbox" className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50" checked={editedNote.notify.clients} onChange={() => handleNotifyChange('clients')} data-id="element-1076" />
                <span className="ml-2 text-sm text-gray-700" data-id="element-1077">Clients</span>
              </label>
            </div>
          </div>
          {/* User information */}
          <div className="flex items-center pt-4 border-t border-gray-200" data-id="element-1078">
            <img src={editedNote.user.avatar} alt={editedNote.user.name} className="w-8 h-8 rounded-full mr-2" data-id="element-1079" />
            <div className="text-sm" data-id="element-1080">
              <p className="font-medium text-gray-900" data-id="element-1081">
                {editedNote.user.name}
              </p>
              <p className="text-gray-500" data-id="element-1082">Will be updated as the author</p>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default Notes;